import { clsx } from 'clsx'
import * as React from 'react'
import { useId } from 'react'

import { useControllableState } from '../../hooks/useControllableState'
import { UnstyledButton } from '../UnstyledButton'
import { Text } from '../Text'
import { MinusIcon, PlusIcon } from '../icons'

import styles from './CounterInput.module.css'

type CounterInputProps = Omit<
  React.ComponentPropsWithoutRef<'input'>,
  'type' | 'value' | 'onChange' | 'size'
> & {
  /**
   * Size of the input counter
   * @default 'medium'
   */
  size?: 'small' | 'medium' | 'large'
  /**
   * Current value
   */
  value: number
  /**
   * Default value
   */
  defaultValue?: number
  /**
   * Minimum value allowed
   * @default 0
   */
  min?: number
  /**
   * Maximum value allowed
   */
  max?: number
  /**
   * Step value for increment/decrement
   * @default 1
   */
  step?: number
  /**
   * Callback when value changes
   */
  onChange: (value: number) => void
  /**
   * Optional suffix content to display after the input value
   */
  suffix?: React.ReactNode
  /**
   * Custom message to display when minimum value is reached
   */
  minMessage?: string
  /**
   * Custom message to display when maximum value is reached
   */
  maxMessage?: string
}

const iconSizebySize = {
  small: 'xsmall',
  medium: 'small',
  large: 'medium',
} as const

const CounterInput = React.forwardRef<HTMLInputElement, CounterInputProps>(
  (
    {
      className,
      style,
      name,
      size = 'medium',
      value: _value,
      defaultValue,
      min = 0,
      max,
      step = 1,
      onChange: _onChange,
      disabled,
      minMessage,
      maxMessage,
      suffix,
      ...props
    }: CounterInputProps,
    forwardedRef,
  ) => {
    const [value, onChange] = useControllableState({
      prop: _value,
      defaultProp: defaultValue,
      onChange: _onChange,
    })
    const [inputValue, setInputValue] = React.useState(value?.toString() ?? '')
    const hintId = useId()

    // Sync inputValue with value prop changes
    React.useEffect(() => {
      setInputValue(value?.toString() ?? '')
    }, [value])

    const handleChangeValue = (nextValue: number) => {
      let value = nextValue

      if (!isNaN(value)) {
        if (value < min) {
          value = min
        }
        if (max !== undefined && value > max) {
          value = max
        }
      }

      onChange(value)
    }

    const handleDecrement = () => {
      if (disabled) return
      handleChangeValue((value ?? 0) - step)
    }

    const handleIncrement = () => {
      if (disabled) return
      handleChangeValue((value ?? 0) + step)
    }

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (disabled) return

      switch (event.key) {
        case 'ArrowUp':
        case 'ArrowRight':
          event.preventDefault()
          handleIncrement()
          break
        case 'ArrowDown':
        case 'ArrowLeft':
          event.preventDefault()
          handleDecrement()
          break
        case 'Home':
          event.preventDefault()
          if (min !== undefined) {
            handleChangeValue(min)
          }
          break
        case 'End':
          event.preventDefault()
          if (max !== undefined) {
            handleChangeValue(max)
          }
          break
      }
    }

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const newInputValue = e.target.value
      setInputValue(newInputValue)

      if (newInputValue === '') {
        // Allow empty input during typing
        return
      }

      const numericValue = parseInt(newInputValue, 10)
      if (!isNaN(numericValue)) {
        handleChangeValue(numericValue)
      }
    }

    const handleBlur = () => {
      // On blur, ensure we have a valid value
      if (inputValue === '' || isNaN(parseInt(inputValue, 10))) {
        const fallbackValue = value ?? min ?? 0
        handleChangeValue(fallbackValue)
        setInputValue(fallbackValue.toString())
      }
    }

    const displayMinMessage = minMessage ?? `Min number is ${min}`
    const displayMaxMessage = maxMessage ?? `Max number is ${max}`

    return (
      <div className={clsx(styles.base, styles[size], className)} style={style}>
        <UnstyledButton
          type="button"
          className={styles.button}
          onClick={handleDecrement}
          disabled={disabled || (min !== undefined && (value ?? 0) <= min)}
          aria-label="Decrease value"
        >
          <MinusIcon size={iconSizebySize[size]} />
        </UnstyledButton>
        <div className={styles.inputWrapper}>
          <input
            type="text"
            name={name}
            // oxlint-disable-next-line jsx-a11y/no-redundant-roles -- input is type="text" (implicit role: textbox); spinbutton is a deliberate AT override for the numeric counter UI
            role="spinbutton"
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={value}
            inputMode="numeric"
            pattern="[0-9]*"
            value={inputValue}
            onChange={handleChange}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            aria-describedby={hintId}
            {...props}
            className={styles.input}
            ref={forwardedRef}
          />
          {suffix && <span>{suffix}</span>}
        </div>
        <UnstyledButton
          type="button"
          className={styles.button}
          onClick={handleIncrement}
          disabled={disabled || (max !== undefined && (value ?? 0) >= max)}
          aria-label="Increase value"
        >
          <PlusIcon size={iconSizebySize[size]} />
        </UnstyledButton>

        {(value === max ||
          (value !== undefined && min !== undefined && value < min)) && (
          <Text size="sm" color="subtle" id={hintId} className={styles.hint}>
            {value === max ? displayMaxMessage : displayMinMessage}
          </Text>
        )}
      </div>
    )
  },
)

CounterInput.displayName = 'CounterInput'

export { CounterInput }
export type { CounterInputProps }
